import { useMergeRefs } from '@floating-ui/react';
import { isElementScrollbarVisible } from '@helpers/utils/dom/DOM';
import { type Column, useTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  type AppTableFeatures,
  appTableFeatures,
} from '../react-table-features';

import styles from './RevampTable.module.scss';
import type {
  RevampTableCompositionProps,
  RevampTableProps,
  RevampTableRow,
} from './RevampTable.types';
import { RevampTableBodyRow } from './RevampTableBodyRow';
import { RevampTableHeaderRow } from './RevampTableHeaderRow';
import {
  createRevampTableColumns,
  getColumnPinConfig,
  getPinnedColumnIds,
  getTableMaxHeight,
} from './utils';

/**
 * Data mode: an opinionated table built from `columnsMeta` + row data,
 * composed from the styled table root, `TableCell`, and `TableHeader`.
 */
const RevampTableData = forwardRef<HTMLTableElement, RevampTableProps>(
  (
    {
      className,
      columnsMeta,
      data,
      stickyHeader = false,
      showBorder = true,
      customHeader,
      sliceIndex,
      floatingExpandBtn,
      isTouchDevice = false,
      withBodyRowHover = false,
      onRowClick,
    },
    forwardedRef
  ) => {
    const internalRef = useRef<HTMLTableElement>(null);
    const tableRef = useMergeRefs([forwardedRef, internalRef]);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const columns = useMemo(
      () => createRevampTableColumns(columnsMeta),
      [columnsMeta]
    );
    const pinnedColumns = useMemo(
      () => getPinnedColumnIds(columnsMeta),
      [columnsMeta]
    );
    const slicedData = useMemo(() => {
      return sliceIndex !== undefined ? data.slice(0, sliceIndex) : data;
    }, [sliceIndex, data]);
    const tableMaxHeight = getTableMaxHeight({
      rowCount: slicedData.length,
      isTouchDevice,
    });

    const table = useTable({
      features: appTableFeatures,
      data: slicedData,
      columns,
      state: {
        columnPinning: {
          start: pinnedColumns,
          end: [],
        },
      },
      initialState: {
        pagination: { pageIndex: 0, pageSize: Infinity },
      },
    });

    const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

    useLayoutEffect(() => {
      const wrapperElement = wrapperRef.current;
      const tableElement = internalRef.current;

      if (!wrapperElement || !tableElement) {
        return;
      }

      // The horizontal scrollbar lives on the wrapper (overflow: auto), not
      // the table, so measure the wrapper. Observe both so a resize of either
      // the scroll container or the table content recomputes the state.
      const updateScrollState = () =>
        setHasHorizontalScroll(isElementScrollbarVisible(wrapperRef));

      updateScrollState();

      const observer = new ResizeObserver(updateScrollState);
      observer.observe(wrapperElement);
      observer.observe(tableElement);

      return () => observer.disconnect();
    }, []);

    const getPinConfig = (column: Column<AppTableFeatures, RevampTableRow>) => {
      if (!pinnedColumns.length) {
        return undefined;
      }

      return getColumnPinConfig(column, hasHorizontalScroll);
    };
    const { rows } = table.getRowModel();

    return (
      <div className={clsx(styles.container, className)}>
        <div>{customHeader}</div>
        <div
          ref={wrapperRef}
          className={styles.wrapper}
          data-revamp-table-wrapper
          data-show-border={showBorder}
          data-custom-header={!!customHeader || undefined}
          style={
            tableMaxHeight ? { maxHeight: `${tableMaxHeight}px` } : undefined
          }
        >
          <table
            ref={tableRef}
            className={clsx(
              styles.root,
              styles.styledRevampTable,
              withBodyRowHover && styles.withBodyRowHover,
              !!onRowClick && styles.withBodyRowCursor
            )}
            data-revamp-table
            data-custom-header={!!customHeader || undefined}
          >
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <RevampTableHeaderRow
                  key={headerGroup.id}
                  getPinConfig={getPinConfig}
                  headerGroup={headerGroup}
                  stickyHeader={!!stickyHeader}
                />
              ))}
            </thead>
            <tbody>
              {rows.map(row => (
                <RevampTableBodyRow
                  key={row.id}
                  row={row}
                  onRowClick={onRowClick}
                />
              ))}
            </tbody>
          </table>
          {sliceIndex !== undefined && data.length > sliceIndex && (
            <div className={styles.buttonWrapper}>{floatingExpandBtn}</div>
          )}
        </div>
      </div>
    );
  }
);

RevampTableData.displayName = 'RevampTableData';

/**
 * Single design-system table. Pass `columnsMeta` + `data` for the opinionated
 * data-driven table, or pass `children` to compose a styled `<table>` yourself
 * (composition mode — the former `RevampTableRoot`, hover/cursor default `true`).
 */
export const RevampTable = forwardRef<
  HTMLTableElement,
  RevampTableProps | RevampTableCompositionProps
>((props, ref) => {
  if ('columnsMeta' in props) {
    return <RevampTableData ref={ref} {...props} />;
  }

  const {
    className,
    withBodyRowHover = true,
    withBodyRowCursor = withBodyRowHover,
    children,
    ...rest
  } = props;

  return (
    <table
      ref={ref}
      className={clsx(
        styles.root,
        withBodyRowHover && styles.withBodyRowHover,
        withBodyRowCursor && styles.withBodyRowCursor,
        className
      )}
      {...rest}
    >
      {children}
    </table>
  );
});

RevampTable.displayName = 'RevampTable';
